import { Dialog } from '@kobalte/core/dialog'
import { createStore } from 'solid-js/store'
import reminderDialogHeaderImage from '~/assets/images/reminder-dialog-header.png'
import { m } from '~/paraglide/messages'
import { native } from '@forest/native-services'

interface State {
  isOpen: boolean
  trialEndDate: string
}

const [reminderDialogState, setReminderDialogState] = createStore<State>({
  isOpen: false,
  trialEndDate: '',
})

let resolvePromise: (() => void) | null = null

export const reminderDialog = {
  /**
   * Opens the reminder dialog and returns a promise that resolves when the user
   * either accepts or dismisses the reminder.
   */
  open: (trialEndDate: string): Promise<void> => {
    return new Promise((resolve) => {
      resolvePromise = resolve
      setReminderDialogState({
        isOpen: true,
        trialEndDate,
      })
    })
  },

  onRemind: async () => {
    try {
      await native.permission.requestNotificationPermission()
    } catch {
      // ignore
    }
    reminderDialog.close()
  },

  onDismiss: () => {
    reminderDialog.close()
  },

  close: () => {
    setReminderDialogState('isOpen', false)
    resolvePromise?.()
    resolvePromise = null
  },
}

export function ReminderDialog() {
  return (
    <Dialog
      open={reminderDialogState.isOpen}
      onOpenChange={(isOpen) => !isOpen && reminderDialog.close()}
    >
      <Dialog.Portal>
        <Dialog.Overlay class="fixed inset-0 z-40 bg-black/50 data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0" />
        <Dialog.Content
          class="fixed left-1/2 top-1/2 z-50 w-[300px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg bg-white data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          style={{
            '--tw-enter-translate-x': '-50%',
            '--tw-enter-translate-y': '-50%',
            '--tw-exit-translate-x': '-50%',
            '--tw-exit-translate-y': '-50%',
          }}
        >
          <img
            src={reminderDialogHeaderImage}
            alt=""
            class="h-[140px] w-full object-cover"
          />

          <div class="flex flex-col items-center gap-4 px-5 pb-5 pt-4">
            <div class="flex flex-col items-center gap-3">
              <Dialog.Title class="text-center text-lg font-semibold text-[#333]">
                {m.dialog_trial_end_notification_title()}
              </Dialog.Title>

              <Dialog.Description class="text-center text-sm text-[#666]">
                {m.dialog_trial_end_notification_content({
                  date: reminderDialogState.trialEndDate,
                })}
              </Dialog.Description>
            </div>

            <div class="flex w-full flex-col items-center gap-3">
              <button
                class="h-10 w-[250px] rounded bg-[#8EC925] text-base text-white"
                style={{
                  'box-shadow': '0px 4px 0px 0px #4A8003',
                }}
                onClick={reminderDialog.onRemind}
              >
                {m.btn_remind_me()}
              </button>

              <button
                class="h-9 text-base font-bold text-[#888]"
                onClick={reminderDialog.onDismiss}
              >
                {m.btn_no_need_reminder()}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}
